import {
  ArrowRightIcon,
  CheckIcon,
  CommandLineIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../context/OrganizationContext";

export const AIAssistantHolding: React.FC = () => {
  const { selectedOrg: organization } = useOrganization();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
            <SparklesIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            AI Assistant - Now Better Than Ever
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            After releasing our first alpha version, we&apos;re completely
            redesigning the AI experience to be more powerful and flexible.
          </p>
          <br />
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            First step: adding the ability to use Claude Desktop.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0">
              <CommandLineIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                Introducing Claude Desktop Integration
              </h2>
              <p className="text-gray-600 mb-4">
                We&apos;ve integrated the entire Spotlight platform with Claude
                Desktop using the Model Context Protocol (MCP). This gives you a
                dramatically better experience:
              </p>
            </div>
          </div>

          {/* Benefits List */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              "Full conversational AI with context awareness",
              "Direct manipulation of your data",
              "Automated research and analysis",
              "Bulk operations and updates",
              "Strategic planning assistance",
              "Real-time collaboration",
            ].map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Ready to Get Started?
            </h3>
            <p className="text-gray-600 mb-4">
              You can use Claude Desktop or any other MCP-compatible client of
              your choice. Setup takes less than 5 minutes.
            </p>
            <button
              onClick={() => navigate("/claude-instructions")}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors group"
            >
              View Setup Instructions
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-gray-900 mb-2">
              🚀 More Powerful
            </h3>
            <p className="text-gray-600 text-sm">
              Access to Claude&apos;s full capabilities with deep integration
              into your Spotlight data.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-gray-900 mb-2">
              🔄 Always Current
            </h3>
            <p className="text-gray-600 text-sm">
              Automatically uses the latest AI models and features as they
              become available.
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-gray-900 mb-2">
              🎯 Context Aware
            </h3>
            <p className="text-gray-600 text-sm">
              Understands your organization&apos;s strategy, segments, and
              propositions for targeted assistance.
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <SparklesIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">
                Why the Change?
              </h4>
              <p className="text-blue-800 text-sm">
                By integrating with Claude Desktop via MCP, we can provide you
                with a much more sophisticated AI experience than what&apos;s
                possible through a web interface alone. You get the full power
                of conversational AI with complete access to your strategic
                data, all in a seamless, native experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantHolding;
